/**
 * previewTheme — shared tokens + helpers for the redesign preview surfaces.
 *
 * The preview pages (/preview/*) render the supplied design templates
 * pixel-faithfully using inline styles, so fidelity does not depend on the
 * Tailwind font map or the CSS-var token layer. This module is the single
 * source of truth for the light/dark palettes (light = exact Daylight-template
 * values; dark = the canonical index.css dark token set), the type stack, and
 * the chip/format helpers every preview screen reuses.
 *
 * Design discipline (per CLAUDE.md): black + orange only. Orange is the single
 * action signal; green means "done" ONLY; everything else grayscale. No blue /
 * teal / purple / red.
 */

import React, { useCallback, useState } from 'react';
import { formatCurrency } from '@/utils/financialFormat';

// ---- type stack (loaded via index.css @import) ----
export const SERIF = "'DM Serif Display', Georgia, serif";
export const BODY = "'Outfit', system-ui, sans-serif";
export const MONO = "'DM Mono', ui-monospace, monospace";

export const THEME_KEY = 'cs-preview-theme';
export type Theme = 'light' | 'dark';

/**
 * Global zoom for the preview surfaces. The templates are authored at compact
 * pixel sizes that read small on large displays; 1.25 == the browser-zoom level
 * Tyler found comfortable (100% → 125%). CSS `zoom` mimics browser zoom exactly
 * (reflows, keeps 100vh == one screen) — applied on each preview page root.
 * Single knob: change here to retune every preview screen.
 */
export const PREVIEW_ZOOM = 1.25;

export type ChipKind = 'orange' | 'green' | 'neutral' | 'ghost';

export interface Palette {
  pageBg: string; cardBg: string; cardAltBg: string; elevBg: string; hoverBg: string;
  railHairline: string; cardBorder: string; controlBorder: string; divider: string;
  rowDivider: string; accentNeutral: string;
  textPrimary: string; textBody: string; textLabel: string; textMuted: string; textGhost: string;
  orange: string; orangeText: string; orangeHover: string; onOrange: string;
  orangeTintBg: string; orangeTintBd: string; orangeRowTint: string;
  greenText: string; greenTintBg: string; greenTintBd: string; greenSolid: string;
  neutralChipBg: string; neutralChipFg: string; neutralChipBd: string;
  ghostChipBg: string; ghostChipFg: string; ghostChipBd: string;
  kbdBg: string; overlayBg: string; cmdkPanelBg: string; cmdkItemHover: string; shadow: string;
}

// light values are pixel-exact to the Daylight templates
export const LIGHT: Palette = {
  pageBg: '#FAFAFA', cardBg: '#FFFFFF', cardAltBg: '#FCFCFC', elevBg: '#F4F4F5', hoverBg: '#F0F0F0',
  railHairline: '#EEEEEE', cardBorder: '#E5E5E5', controlBorder: '#E0E0E0', divider: '#ECECEC',
  rowDivider: '#F0F0F0', accentNeutral: '#D4D4D4',
  textPrimary: '#0A0A0A', textBody: '#333333', textLabel: '#666666', textMuted: '#A3A3A3', textGhost: '#C8C8C8',
  orange: '#FF6600', orangeText: '#C2410C', orangeHover: '#FF7A1F', onOrange: '#fefefe',
  orangeTintBg: 'rgba(255,102,0,0.12)', orangeTintBd: 'rgba(255,102,0,0.28)', orangeRowTint: 'rgba(255,102,0,0.05)',
  greenText: '#16A34A', greenTintBg: 'rgba(34,197,94,0.12)', greenTintBd: 'rgba(34,197,94,0.25)', greenSolid: '#22C55E',
  neutralChipBg: '#EEEEEE', neutralChipFg: '#555555', neutralChipBd: '#D4D4D4',
  ghostChipBg: '#F4F4F5', ghostChipFg: '#A3A3A3', ghostChipBd: '#ECECEC',
  kbdBg: '#FAFAFA', overlayBg: 'rgba(0,0,0,0.55)', cmdkPanelBg: '#FFFFFF', cmdkItemHover: '#ECECEC', shadow: 'rgba(0,0,0,0.25)',
};

// dark values are the canonical index.css dark token set
export const DARK: Palette = {
  pageBg: '#0A0A0A', cardBg: '#121212', cardAltBg: '#141414', elevBg: '#161616', hoverBg: '#181818',
  railHairline: '#1C1C1C', cardBorder: '#232323', controlBorder: '#2A2A2A', divider: '#1E1E1E',
  rowDivider: '#1A1A1A', accentNeutral: '#2E2E2E',
  textPrimary: '#FFFFFF', textBody: '#CCCCCC', textLabel: '#888888', textMuted: '#5A5A5A', textGhost: '#3A3A3A',
  orange: '#FF6600', orangeText: '#FF6600', orangeHover: '#FF7A1F', onOrange: '#fefefe',
  orangeTintBg: 'rgba(255,102,0,0.14)', orangeTintBd: 'rgba(255,102,0,0.30)', orangeRowTint: 'rgba(255,102,0,0.07)',
  greenText: '#22C55E', greenTintBg: 'rgba(34,197,94,0.12)', greenTintBd: 'rgba(34,197,94,0.25)', greenSolid: '#22C55E',
  neutralChipBg: '#232323', neutralChipFg: '#888888', neutralChipBd: '#2E2E2E',
  ghostChipBg: '#161616', ghostChipFg: '#5A5A5A', ghostChipBd: '#1E1E1E',
  kbdBg: '#0A0A0A', overlayBg: 'rgba(0,0,0,0.72)', cmdkPanelBg: '#161616', cmdkItemHover: '#1E1E1E', shadow: 'rgba(0,0,0,0.7)',
};

/** Theme state hook with localStorage persistence (shared across preview pages). */
export function useTheme(): { theme: Theme; P: Palette; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    try { return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  const toggle = useCallback(() => {
    setTheme((t) => {
      const next: Theme = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(THEME_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);
  return { theme, P: theme === 'dark' ? DARK : LIGHT, toggle };
}

/** Pill chip — monochrome + orange; green = done only. */
export const chipStyle = (kind: ChipKind, P: Palette): React.CSSProperties => {
  const map: Record<ChipKind, { bg: string; fg: string; bd: string }> = {
    orange: { bg: P.orangeTintBg, fg: P.orangeText, bd: P.orangeTintBd },
    green: { bg: P.greenTintBg, fg: P.greenText, bd: P.greenTintBd },
    neutral: { bg: P.neutralChipBg, fg: P.neutralChipFg, bd: P.neutralChipBd },
    ghost: { bg: P.ghostChipBg, fg: P.ghostChipFg, bd: P.ghostChipBd },
  };
  const c = map[kind];
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
    padding: '3px 10px', borderRadius: 999, background: c.bg, color: c.fg,
    border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
  };
};

/** Small ☀/☾ theme toggle button used in preview topbars. */
export function ThemeToggle({ P, theme, onToggle }: { P: Palette; theme: Theme; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      title={theme === 'dark' ? 'Switch to Daylight' : 'Switch to Dark'}
      style={{ width: 36, height: 36, borderRadius: 9, border: `1px solid ${P.cardBorder}`, background: P.elevBg, color: P.textBody, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >{theme === 'dark' ? '☀' : '☾'}</button>
  );
}

export const fmtUSD = (n: number): string => formatCurrency(n);

export function relTime(iso?: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.round(days / 7)}w ago`;
}
